/**
 * Theme Store — Zustand store for dark mode
 * Persists to localStorage, applies .dark class to <html>
 * Syncs to server (debounced) when user explicitly changes theme.
 */

import { create } from 'zustand';
import { api } from '../api/client';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
  applyServerTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  initTheme: () => void;
}

// Debounced server sync — fires 800ms after the last toggle, fire-and-forget.
let _themeSyncTimer: ReturnType<typeof setTimeout> | null = null;
function syncThemeToServer(theme: ThemeMode): void {
  if (_themeSyncTimer) {clearTimeout(_themeSyncTimer);}
  _themeSyncTimer = setTimeout(() => {
    api.preferences.setTheme(theme).catch(() => {});
  }, 800);
}

const STORAGE_KEY = 'credia-theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {return 'light';}
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.add('theme-transition');
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  setTimeout(() => root.classList.remove('theme-transition'), 300);
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {return getSystemTheme();}
  return mode;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: 'light',
  resolvedTheme: 'light',

  // User explicitly picked a theme — persist locally and sync to server.
  setTheme: (theme) => {
    const resolved = resolveTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    applyTheme(resolved);
    set({ theme, resolvedTheme: resolved });
    syncThemeToServer(theme);
  },

  // Server returned a saved theme on login — apply it without triggering another sync.
  applyServerTheme: (theme) => {
    const resolved = resolveTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    applyTheme(resolved);
    set({ theme, resolvedTheme: resolved });
  },

  toggleTheme: () => {
    const current = get().theme;
    const next: ThemeMode = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    get().setTheme(next);
  },

  initTheme: () => {
    // Check for theme param from landing page redirect (cross-subdomain handoff).
    // If present, adopt it and persist — prevents flash when navigating from
    // credia.pro (dark default) to app.credia.pro.
    const urlTheme = new URLSearchParams(window.location.search).get('theme') as ThemeMode | null;
    if (urlTheme && (urlTheme === 'light' || urlTheme === 'dark')) {
      localStorage.setItem(STORAGE_KEY, urlTheme);
      // Clean the URL param so it doesn't persist on refresh/bookmarks
      const url = new URL(window.location.href);
      url.searchParams.delete('theme');
      window.history.replaceState({}, '', url.toString());
    }

    const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    const theme = stored || 'light';
    const resolved = resolveTheme(theme);
    applyTheme(resolved);
    set({ theme, resolvedTheme: resolved });

    // Listen for system theme changes when in 'system' mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      const state = get();
      if (state.theme === 'system') {
        const newResolved = getSystemTheme();
        applyTheme(newResolved);
        set({ resolvedTheme: newResolved });
      }
    });
  },
}));
